/**
 * Public privacy policy page — RGPD compliance.
 * Accessible at /privacy (no auth required).
 */
export default function PrivacyPolicyScreen() {
  return (
    <div className="min-h-screen bg-white flex flex-col items-center py-12 px-4">
      <div className="max-w-[720px] w-full">
        <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 32 }}>
          Politique de confidentialité
        </h1>

        <Section title="1. Responsable du traitement">
          <p>
            Le responsable du traitement de vos données personnelles est
            l'entreprise exploitant le service Heric (ci-après « le garage » ou « nous »),
            accessible via la plateforme Hexaflow.
          </p>
          <p>
            Pour toute question relative à vos données personnelles, vous pouvez
            nous contacter à l'adresse : <strong>contact@hexaflow.fr</strong>
          </p>
        </Section>

        <Section title="2. Données collectées">
          <p>Lors de la prise de rendez-vous en ligne, nous collectons :</p>
          <ul>
            <li>Nom et prénom</li>
            <li>Adresse email</li>
            <li>Numéro de téléphone</li>
            <li>Immatriculation du véhicule</li>
            <li>Type d'intervention souhaitée</li>
            <li>Dates et horaires de dépôt / restitution</li>
          </ul>
          <p>
            Lors du suivi de votre dossier, nous affichons les informations
            que vous avez fournies ainsi que l'avancement des travaux.
          </p>
        </Section>

        <Section title="3. Finalités du traitement">
          <p>Vos données sont utilisées pour :</p>
          <ul>
            <li>Gérer votre rendez-vous et votre dossier de réparation</li>
            <li>Vous contacter par email, SMS ou WhatsApp concernant votre véhicule</li>
            <li>Établir et envoyer des devis, factures et demandes de paiement</li>
            <li>Vous permettre de suivre l'avancement de votre dossier en ligne</li>
            <li>Améliorer la qualité de nos services</li>
          </ul>
        </Section>

        <Section title="4. Base légale">
          <p>Le traitement de vos données repose sur :</p>
          <ul>
            <li>
              <strong>L'exécution d'un contrat</strong> : la gestion de votre
              rendez-vous et de votre dossier de réparation
            </li>
            <li>
              <strong>Votre consentement</strong> : pour les communications
              marketing (optionnel)
            </li>
            <li>
              <strong>L'intérêt légitime</strong> : pour l'amélioration de nos
              services et la prévention de la fraude (reCAPTCHA)
            </li>
          </ul>
        </Section>

        <Section title="5. Destinataires des données">
          <p>Vos données peuvent être transmises à :</p>
          <ul>
            <li>Le personnel du garage en charge de votre dossier</li>
            <li>Nos sous-traitants techniques (hébergement, envoi d'emails et SMS)</li>
            <li>Les prestataires de paiement (Bridge / Bankin')</li>
            <li>Google reCAPTCHA (protection anti-spam)</li>
          </ul>
          <p>
            Vos données ne sont jamais vendues à des tiers.
          </p>
        </Section>

        <Section title="6. Durée de conservation">
          <ul>
            <li>
              <strong>Données de dossier</strong> : conservées pendant la durée
              de la relation commerciale puis 3 ans après le dernier contact
            </li>
            <li>
              <strong>Factures et paiements</strong> : 10 ans (obligation
              légale comptable)
            </li>
            <li>
              <strong>Données de prospection</strong> : 3 ans à compter du
              dernier contact
            </li>
          </ul>
        </Section>

        <Section title="7. Vos droits">
          <p>
            Conformément au Règlement Général sur la Protection des Données
            (RGPD) et à la loi Informatique et Libertés, vous disposez des
            droits suivants :
          </p>
          <ul>
            <li><strong>Droit d'accès</strong> : obtenir une copie de vos données</li>
            <li><strong>Droit de rectification</strong> : corriger des données inexactes</li>
            <li><strong>Droit à l'effacement</strong> : demander la suppression de vos données</li>
            <li><strong>Droit à la limitation</strong> : restreindre le traitement</li>
            <li><strong>Droit à la portabilité</strong> : récupérer vos données dans un format structuré</li>
            <li><strong>Droit d'opposition</strong> : vous opposer au traitement pour motif légitime</li>
          </ul>
          <p>
            Pour exercer ces droits, envoyez un email à{" "}
            <strong>contact@hexaflow.fr</strong> en précisant votre demande et
            en joignant une pièce d'identité.
          </p>
          <p>
            En cas de litige, vous pouvez introduire une réclamation auprès de
            la CNIL (Commission Nationale de l'Informatique et des Libertés) :
            {" "}<a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">www.cnil.fr</a>
          </p>
        </Section>

        <Section title="8. Cookies et technologies similaires">
          <p>Notre site utilise :</p>
          <ul>
            <li>
              <strong>Google reCAPTCHA v3</strong> : pour protéger les
              formulaires contre le spam. Ce service peut déposer des cookies.
              Consultez les{" "}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                règles de confidentialité
              </a>{" "}
              et les{" "}
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                conditions d'utilisation
              </a>{" "}
              de Google.
            </li>
            <li>
              <strong>Cookies techniques</strong> : nécessaires au
              fonctionnement du site (session, préférences). Ils ne requièrent
              pas de consentement.
            </li>
          </ul>
          <p>
            Aucun cookie publicitaire ou de suivi n'est utilisé.
          </p>
        </Section>

        <Section title="9. Sécurité">
          <p>
            Nous mettons en œuvre des mesures techniques et organisationnelles
            pour protéger vos données : chiffrement des communications (HTTPS),
            contrôle d'accès, authentification sécurisée, hébergement sur
            infrastructure Google Cloud Platform.
          </p>
        </Section>

        <Section title="10. Modifications">
          <p>
            Cette politique de confidentialité peut être mise à jour. La
            version en vigueur est toujours accessible à cette adresse. Dernière
            mise à jour : mai 2026.
          </p>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 10 }}>{title}</h2>
      <div
        style={{ fontSize: 14, lineHeight: "22px", color: "#374151" }}
        className="flex flex-col gap-2 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1"
      >
        {children}
      </div>
    </div>
  );
}
